import Cookies from 'js-cookie';
import type { User } from 'oidc-client-ts';
import { type Dispatch, memo, type SetStateAction, useEffect } from 'react';
import { AuthProvider, hasAuthParams, useAuth } from 'react-oidc-context';
import { when } from '../../../../react-commons/utils/when';
import type { Auth } from './AuthProvider';

export const SetOpenIdContext = ({
    authority,
    client_id,
    setAuth,
}: {
    authority: string;
    client_id: string;
    setAuth: Dispatch<SetStateAction<Auth<boolean> | undefined>>;
}) => {
    return (
        <AuthProvider
            authority={authority}
            client_id={client_id}
            redirect_uri={`${window.location.origin}/auth/oidc/callback/login`}
            scope={'openid profile email'}
            monitorSession={true}
            onSigninCallback={() => window.history.replaceState({}, '', location.pathname)}
        >
            <_SetOpenIdContext setAuthData={setAuth} />
        </AuthProvider>
    );
};

const _SetOpenIdContext = memo(
    ({ setAuthData }: { setAuthData: Dispatch<SetStateAction<Auth<boolean> | undefined>> }) => {
        const {
            isLoading: loading,
            isAuthenticated: authenticated,
            user,
            activeNavigator,
            signoutSilent,
            signinRedirect,
            signinSilent,
        } = useAuth();

        useEffect(() => {
            if (loading) {
                return;
            }
            if (!authenticated) {
                Cookies.remove('access');
            } else if (user?.access_token && user.expires_at) {
                Cookies.set('access', user.access_token, {
                    secure: true,
                    sameSite: 'strict',
                    expires: new Date(user.expires_at * 1000),
                });
            }
        }, [loading, authenticated, user?.access_token, user?.expires_at]);

        const authState =
            authenticated ? 'authenticated'
            : loading ? 'loading'
            : 'unauthenticated';

        useEffect(() => {
            setAuthData((prevValue) => {
                if (prevValue?.authState === authState) {
                    return prevValue;
                }
                return when({
                    expression: authState,
                    cases: {
                        loading: { isOpenId: true, authState: 'loading' },
                        authenticated: {
                            isOpenId: true,
                            authState: 'authenticated',
                            logout: signoutSilent,
                        },
                        unauthenticated: {
                            isOpenId: true,
                            authState: 'unauthenticated',
                            login: signinRedirect,
                            silentLogin: (onSuccess?: (user: User) => void) => {
                                if (!hasAuthParams() && !activeNavigator) {
                                    void signinSilent().then((user) => {
                                        if (user) {
                                            onSuccess?.(user);
                                        }
                                    });
                                }
                            },
                        },
                    },
                });
            });
        }, [authState]);

        return <></>;
    }
);
